"use strict";
/**
 * Delegations are the building blocks of top-down trust structures in KILT. An Attester can inherit trust through delegation from another attester ("top-down").
 * In order to model these trust hierarchies, a delegation is represented as a **node** in a **delegation tree**.
 *
 * A delegation object is stored on-chain, and can be revoked. A base node is created, a ID which may be used in a [[RequestForAttestation]].
 * A delegation can and may restrict permissions.
 *
 * Permissions:
 *   * Delegate.
 *   * Attest.
 *
 * @packageDocumentation
 * @module DelegationBaseNode
 * @preferred
 */
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const Attestation_chain_1 = require("../attestation/Attestation.chain");
const Delegation_chain_1 = require("./Delegation.chain");
const Delegation_utils_1 = tslib_1.__importDefault(require("./Delegation.utils"));
class DelegationBaseNode {
    /**
     * Builds a new [DelegationBaseNode] instance.
     *
     * @param delegationBaseNodeInput - The base object from which to create the delegation base node.
     */
    constructor(delegationBaseNodeInput) {
        this.revoked = false;
        this.account = delegationBaseNodeInput.account;
        this.id = delegationBaseNodeInput.id;
        this.revoked = delegationBaseNodeInput.revoked;
        Delegation_utils_1.default(this);
    }
    /**
     * Fetches and resolves all attestations attested with this delegation node.
     *
     * @returns Promise containing all resolved attestations attested with this node.
     */
    async getAttestations() {
        const attestationHashes = await this.getAttestationHashes();
        const attestations = await Promise.all(attestationHashes.map((claimHash) => {
            return Attestation_chain_1.query(claimHash);
        }));
        return attestations.filter((value) => !!value);
    }
    /**
     * Fetches all hashes of attestations attested with this delegation node.
     *
     * @returns Promise containing all attestation hashes attested with this node.
     */
    async getAttestationHashes() {
        return Delegation_chain_1.getAttestationHashes(this.id);
    }
    /**
     * Checks on chain whether a identity with the given address is delegating to the current node.
     *
     * @param address The address of the identity.
     * @returns An object containing a `node` owned by the identity if it is delegating, plus the number of `steps` traversed. `steps` is 0 if the address is owner of the current node.
     */
    async findAncestorOwnedBy(address) {
        if (this.account === address) {
            return {
                steps: 0,
                node: this,
            };
        }
        const parent = await this.getParent();
        if (parent) {
            const result = await parent.findAncestorOwnedBy(address);
            result.steps += 1;
            return result;
        }
        return {
            steps: 0,
            node: null,
        };
    }
    /**
     * Recursively counts all nodes in the branches below the current node (excluding the current node).
     *
     * @returns Promise resolving to the node count.
     */
    async subtreeNodeCount() {
        const children = await this.getChildren();
        if (children.length > 0) {
            const childrensChildCounts = await Promise.all(children.map((child) => child.subtreeNodeCount()));
            return (children.length +
                childrensChildCounts.reduce((previous, current) => previous + current));
        }
        return 0;
    }
}
exports.default = DelegationBaseNode;
